import { apiErrorMessage } from '@bayesprep/shared-ui/errors';
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import StudentLogo from '@/components/StudentLogo';
import axios from 'axios';

interface TokenValidationResponse {
  valid: boolean;
  email?: string;
  expiresAt?: string;
  minutesUntilExpiration?: number;
  message?: string;
}

interface SetupFormData {
  password: string;
  confirmPassword: string;
}

const StudentSetupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState<SetupFormData>({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState('');
  const [tokenInfo, setTokenInfo] = useState<TokenValidationResponse | null>(null);

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No setup token provided. Please check your email link.');
        setIsValidating(false);
        return;
      }

      try {
        const response = await axios.get<TokenValidationResponse>(
          `/api/auth/validate-student-setup-token?token=${encodeURIComponent(token)}`
        );

        if (response.data.valid) {
          setTokenInfo(response.data);
        } else {
          setError('This setup link is invalid or expired. Ask your institution for a new link.');
        }
      } catch (error: any) {
        console.error('Token validation error:', error);
        setError('Failed to validate setup token. Please try again or contact your administrator.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleInputChange = (field: keyof SetupFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error when user starts typing
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      return 'Password must contain at least one special character (@$!%*?&)';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validate password
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        setError(passwordError);
        return;
      }

      // Check password confirmation
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      // Submit setup
      await axios.post('/api/auth/complete-student-setup', {
        token,
        password: formData.password
      });

      // Success - redirect to login with success message
      navigate('/login', {
        state: {
          message: 'Account setup completed successfully! You can now sign in.',
          email: tokenInfo?.email
        }
      });

    } catch (error: any) {
      console.error('Setup completion error:', error);
      setError(apiErrorMessage(error, 'Failed to complete account setup. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeRemaining = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes > 0 ? `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}` : ''}`;
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600 dark:text-gray-400">Validating your setup link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state - invalid token
  if (!tokenInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-600">Setup Link Invalid</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This could happen if:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 text-left mb-6 space-y-1">
              <li>• The link has expired (links are valid for 8 hours)</li>
              <li>• The link has already been used</li>
              <li>• The link is malformed or incomplete</li>
            </ul>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Go to Login
            </Button>
            <p className="text-xs text-gray-500 mt-4">
              Need help? Contact your administrator for a new setup link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main setup form
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <StudentLogo size="xl" showText={true} textColor="default" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Complete Your Account Setup
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Set up your password for {tokenInfo.email}
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            {/* Time remaining warning */}
            {tokenInfo.minutesUntilExpiration && tokenInfo.minutesUntilExpiration < 60 && (
              <Alert className="mb-4 border-orange-200 bg-orange-50">
                <Clock className="h-4 w-4" />
                <AlertDescription className="text-orange-700">
                  This setup link will expire in {formatTimeRemaining(tokenInfo.minutesUntilExpiration)}.
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300" required>
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your new password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    autoFocus
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                   aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Password must contain:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li>At least 8 characters</li>
                    <li>One uppercase letter</li>
                    <li>One lowercase letter</li>
                    <li>One number</li>
                    <li>One special character (@$!%*?&)</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300" required>
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                   aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'} aria-pressed={showConfirmPassword} >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !formData.password || !formData.confirmPassword}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up account...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Complete Setup
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Having trouble? Contact your administrator for assistance.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentSetupPage;
